if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        if (typeof this.onVariantChangeUnsubscriber === 'function') {
          this.onVariantChangeUnsubscriber();
        }
        if (typeof this.cartUpdateUnsubscriber === 'function') {
          this.cartUpdateUnsubscriber();
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          if (html && html.querySelectorAll) {
            html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
          }
        });
        this.postProcessHtmlCallbacks.push((newNode) => {
          if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
            window.Shopify.PaymentButton.init();
          }
          if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
            window.ProductModel.loadShopifyXR();
          }
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          try {
            productForm.toggleSubmitButton(true);
          } catch (e) {
            console.warn('toggleSubmitButton error:', e);
          }
        }
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          try {
            productForm.handleErrorMessage();
          } catch (e) {
            console.warn('handleErrorMessage error:', e);
          }
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const modal = this.productModal;
          if (modal && typeof modal.remove === 'function') {
            modal.remove();
          }

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const productInfoElement = html.querySelector(selector);
          const variant = productInfoElement ? this.getSelectedVariant(productInfoElement) : null;
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const headTitle = document.querySelector('head title');
            const htmlTitle = html.querySelector('head title');
            if (headTitle && htmlTitle) {
              headTitle.innerHTML = htmlTitle.innerHTML;
            }

            const mainElement = document.querySelector('main');
            const htmlMain = html.querySelector('main');
            if (mainElement && htmlMain && typeof HTMLUpdateUtility !== 'undefined') {
              HTMLUpdateUtility.viewTransition(
                mainElement,
                htmlMain,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          } else {
            const htmlProductInfo = html.querySelector('product-info');
            if (htmlProductInfo && typeof HTMLUpdateUtility !== 'undefined') {
              HTMLUpdateUtility.viewTransition(
                this,
                htmlProductInfo,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            const targetElement = document.querySelector(`#${targetId}`);
            if (targetElement && typeof targetElement.focus === 'function') {
              targetElement.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const variantElement = productInfoNode.querySelector('variant-selects [data-selected-variant]');
        const selectedVariant = variantElement ? variantElement.innerHTML : null;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(`section_id=${this.sectionId}`);
        }

        if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html ? html.querySelector('variant-selects') : null;
        if (variantSelects && this.variantSelectors && typeof HTMLUpdateUtility !== 'undefined') {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          const pickupAvail = this.pickupAvailability;
          if (pickupAvail && typeof pickupAvail.update === 'function') {
            pickupAvail.update(variant);
          }
          
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          
          const quantityRules = this.querySelector(`#Quantity-Rules-${this.dataset.section}`);
          if (quantityRules) quantityRules.classList.remove('hidden');
          
          const volumeNote = this.querySelector(`#Volume-Note-${this.dataset.section}`);
          if (volumeNote) volumeNote.classList.remove('hidden');

          const productForm = this.productForm;
          if (productForm && typeof productForm.toggleSubmitButton === 'function') {
            const submitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
            const isDisabled = submitButton ? submitButton.hasAttribute('disabled') : true;
            const soldOutText = window.variantStrings ? window.variantStrings.soldOut : 'Sold Out';
            try {
              productForm.toggleSubmitButton(isDisabled, soldOutText);
            } catch (e) {
              console.warn('toggleSubmitButton error:', e);
            }
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          }
        };
      }

      updateVariantInputs(variantId) {
        const forms = this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        );
        forms.forEach((productForm) => {
          const input = productForm ? productForm.querySelector('input[name="id"]') : null;
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        const shareButton = this.querySelector('share-button');
        if (shareButton && typeof shareButton.updateUrl === 'function') {
          const shopUrl = window.shopUrl || '';
          shareButton.updateUrl(`${shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        }

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const productForm = this.productForm;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          const unavailableText = window.variantStrings ? window.variantStrings.unavailable : 'Unavailable';
          try {
            productForm.toggleSubmitButton(true, unavailableText);
          } catch (e) {
            console.warn('toggleSubmitButton error:', e);
          }
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach((el) => {
          if (el && el.classList) el.classList.add('hidden');
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html ? html.querySelector('media-gallery ul') : null;

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover') && typeof enableZoomOnHover === 'function') {
            enableZoomOnHover(2);
          }
          const mediaGallerySourceItems = mediaGallerySource ? Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]')) : [];
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        }

        const modal = this.productModal;
        const modalContent = modal ? modal.querySelector('.product-media-modal__content') : null;
        const newModalContent = html ? html.querySelector('product-modal .product-media-modal__content') : null;
        if (modalContent && newModalContent) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      fetchQuantityRules() {
        const productForm = this.productForm;
        const variantIdInput = productForm ? productForm.variantIdInput : null;
        const currentVariantId = variantIdInput ? variantIdInput.value : null;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html ? html.getElementById(`Quantity-Form-${sectionId}`) : null;
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector('product-form') || null;
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`) || null;
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability') || null;
      }

      get variantSelectors() {
        return this.querySelector('variant-selects') || null;
      }

      get relatedProducts() {
        if (typeof SectionId === 'undefined') return null;
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`) || null;
      }

      get quickOrderList() {
        if (typeof SectionId === 'undefined') return null;
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`) || null;
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}